<template>
  <!-- eslint-disable max-len -->
  <section>
    <div class="verify-info-edit-desktop">
      <form
        ref="form"
        @submit.prevent="validate(); analytics.form.submitted();"
      >
        <div class="c-hairline mt-8" />
        <div class="py-8 flex flex-row flex-no-wrap">
          <div class="flex-1">
            <div class="max:pr-l">
              <h3>Move Information</h3>
              <InputDropdown
                v-model="moverType"
                label="Who's Moving"
                name="mover-type"
                class="mt-4"
                :has-error="errors.moverType.hasError"
                :disabled="ivsProofingCompleted"
                @input="$event && analytics.click.moverType($event);"
              >
                <option
                  value=""
                  aria-describedby="mover-type-field"
                >
                  Select
                </option>
                <option value="INDIVIDUAL">
                  Individual
                </option>
                <option value="FAMILY">
                  Family
                </option>
                <option value="BUSINESS">
                  Business
                </option>
                <span
                  v-if="errors.moverType.hasError"
                  id="moverType-error"
                  slot="helper"
                >
                  {{ errors.moverType.message }}
                </span>
              </InputDropdown>
              <template v-if="moverType === 'FAMILY'">
                <div
                  class="mt-6 flex flex-row"
                  role="group"
                  aria-labelledby="family-same-last-name"
                >
                  <p
                    id="family-same-last-name"
                    class="flex-auto c-input-label mr-3"
                  >
                    Do all individuals moving from this address share the same last name?
                  </p>
                  <InputRadio
                    v-model="familySameLastName"
                    form-value="YES"
                    name="family-same-last-name"
                    class="flex-shrink"
                    :has-error="errors.familySameLastName.hasError"
                    :disabled="ivsProofingCompleted"
                    @input="familySameLastNameHandle('YES'); analytics.input.familySameLastName('YES');"
                  >
                    Yes
                  </InputRadio>
                  <InputRadio
                    v-model="familySameLastName"
                    form-value="NO"
                    name="family-same-last-name"
                    class="flex-shrink"
                    :has-error="errors.familySameLastName.hasError"
                    :disabled="ivsProofingCompleted"
                    @input="familySameLastNameHandle('NO'); analytics.input.familySameLastName('NO');"
                  >
                    No
                  </InputRadio>
                </div>
                <p
                  :class="{ 'errors': errors.familySameLastName.hasError }"
                  class="c-input-helper"
                >
                  {{ errors.familySameLastName.message }}
                </p>

                <div
                  class="mt-3 flex flex-row"
                  role="group"
                  aria-labelledby="family-remaining"
                >
                  <p
                    id="family-remaining"
                    class="flex-auto c-input-label mr-3"
                  >
                    Is anyone with this same last name remaining at the address?
                  </p>
                  <InputRadio
                    v-model="familyRemaining"
                    form-value="YES"
                    name="family-remaining"
                    class="flex-shrink"
                    :has-error="errors.familyRemaining.hasError"
                    :disabled="ivsProofingCompleted"
                    @input="familyRemainingHandle('YES'); analytics.input.familyRemaining('YES');"
                  >
                    Yes
                  </InputRadio>
                  <InputRadio
                    v-model="familyRemaining"
                    form-value="NO"
                    name="family-remaining"
                    class="flex-shrink"
                    :has-error="errors.familyRemaining.hasError"
                    :disabled="ivsProofingCompleted"
                    @input="familyRemainingHandle('NO'); analytics.input.familyRemaining('NO');"
                  >
                    No
                  </InputRadio>
                </div>
                <p
                  :class="{ 'errors': errors.familyRemaining.hasError }"
                  class="c-input-helper"
                >
                  {{ errors.familyRemaining.message }}
                </p>
              </template>
              <template v-if="moverType === 'BUSINESS'">
                <div
                  class="mt-6 flex flex-row"
                  role="group"
                  aria-labelledby="business-wholeCompany"
                >
                  <p
                    id="business-wholeCompany"
                    class="flex-auto c-input-label"
                  >
                    Is the ENTIRE business moving?
                  </p>
                  <InputRadio
                    v-model="businessWholeCompany"
                    form-value="YES"
                    name="business-whole-company"
                    class="flex-shrink"
                    :has-error="errors.businessWholeCompany.hasError"
                    @input="businessWholeCompanyHandle('YES'); analytics.input.businessWholeCompany('YES');"
                  >
                    Yes
                  </InputRadio>
                  <InputRadio
                    v-model="businessWholeCompany"
                    form-value="NO"
                    name="business-whole-company"
                    class="flex-shrink"
                    :has-error="errors.businessWholeCompany.hasError"
                    @input="businessWholeCompanyHandle('NO'); analytics.input.businessWholeCompany('NO');"
                  >
                    No
                  </InputRadio>
                </div>
                <p
                  :class="{ 'errors': errors.businessWholeCompany.hasError }"
                  class="c-input-helper"
                >
                  {{ errors.businessWholeCompany.message }}
                </p>
                <InputText
                  v-model.trim="businessName"
                  label="Business Name"
                  placeholder="Business Name"
                  name="business-name"
                  class="mt-3"
                  :has-error="errors.businessName.hasError"
                  @blur="analytics.input.businessName();"
                  @input="errors.businessName.hasError = false"
                >
                  <span
                    v-if="errors.businessName.hasError"
                    slot="helper"
                  >
                    {{ errors.businessName.message }}
                  </span>
                </InputText>
                <!-- Business Specific Inputs -->
                <template v-if="flagBusinessAliasEnabled && showBusinessAlias">
                  <div class="c-hairline mt-5 mb-5" />
                  <BusinessAlias
                    v-if="enhancedBusinessAliasesEnabled"
                    ref="businessAliases"
                    :value="businessAliases"
                    :field-error="fieldErrors"
                    :analytics="analytics"
                    :business-name="businessName"
                    :initial-alias-count="enhancedBusinessAliasesInitialCount"
                    :aliases-increment="enhancedBusinessAliasesIncrementCount"
                    :max-alias-count="enhancedBusinessAliasesMaxCount"
                  />
                  <template v-if="!enhancedBusinessAliasesEnabled">
                    <InputText
                      v-model.trim="businessAlias1"
                      label="Business Alias 1"
                      placeholder="Business Alias 1"
                      name="business-alias-1"
                      class="mt-2"
                      :has-error="errors.businessAlias1.hasError"
                      @blur="analytics.input.businessAlias('1');"
                    >
                      <span
                        v-if="errors.businessAlias1.hasError"
                        slot="helper"
                      >
                        {{ errors.businessAlias1.message }}
                      </span>
                    </InputText>
                    <InputText
                      v-model.trim="businessAlias2"
                      label="Business Alias 2"
                      placeholder="Business Alias 2"
                      name="business-alias-2"
                      class="mt-2"
                      :has-error="errors.businessAlias2.hasError"
                      @blur="analytics.input.businessAlias('2');"
                    >
                      <span
                        v-if="errors.businessAlias2.hasError"
                        slot="helper"
                      >
                        {{ errors.businessAlias2.message }}
                      </span>
                    </InputText>
                    <InputText
                      v-model.trim="businessAlias3"
                      label="Business Alias 3"
                      placeholder="Business Alias 3"
                      name="business-alias-3"
                      class="mt-2"
                      :has-error="errors.businessAlias3.hasError"
                      @blur="analytics.input.businessAlias('3');"
                    >
                      <span
                        v-if="errors.businessAlias3.hasError"
                        slot="helper"
                      >
                        {{ errors.businessAlias3.message }}
                      </span>
                    </InputText>
                    <InputText
                      v-model.trim="businessAlias4"
                      label="Business Alias 4"
                      placeholder="Business Alias 4"
                      name="business-alias-4"
                      class="mt-2"
                      :has-error="errors.businessAlias4.hasError"
                      @blur="analytics.input.businessAlias('4');"
                    >
                      <span
                        v-if="errors.businessAlias4.hasError"
                        slot="helper"
                      >
                        {{ errors.businessAlias4.message }}
                      </span>
                    </InputText>
                  </template>
                </template>
                <div class="business-alias-buttons">
                  <button
                    v-if="flagBusinessAliasEnabled"
                    id="biz-alias-button"
                    ref="toggleBusinessAliases"
                    type="button"
                    class="text-blue-100 font-bold underline mt-5"
                    @click="showBusinessAlias = !showBusinessAlias; analytics.click.businessAliases(showBusinessAlias);"
                  >
                    <template v-if="showBusinessAlias">
                      My business does not go by another name
                    </template>
                    <template v-else>
                      My business also goes by another name
                    </template>
                  </button>
                  <button
                    v-if="showBusinessAlias && enhancedBusinessAliasesEnabled"
                    id="biz-more-alias-button"
                    type="button"
                    class="biz-more-alias-button text-blue-100 font-bold underline mt-5"
                    @click.prevent="moreAlias()"
                  >
                    <SvgPlusIcon
                      color="#3573b1"
                      :height="12"
                      :width="12"
                    /><span>Add more aliases</span>
                  </button>
                </div>
              </template>
              <!-- Default non-business inputs -->
              <template v-else>
                <InputText
                  v-model.trim="firstName"
                  label="First Name"
                  placeholder="First Name"
                  name="first-name"
                  class="mt-2"
                  :has-error="errors.firstName.hasError"
                  :disabled="ivsProofingCompleted"
                  @blur="analytics.input.firstName();"
                  @input="errors.firstName.hasError = false"
                >
                  <span
                    v-if="errors.firstName.hasError"
                    slot="helper"
                  >
                    {{ errors.firstName.message }}
                  </span>
                </InputText>
                <InputText
                  v-model="middleName"
                  label="Middle Name or Initial"
                  placeholder="Middle"
                  name="middle-initial"
                  class="mt-2"
                  :has-error="middleName ? errors.middleName.hasError : false"
                  :disabled="ivsProofingCompleted"
                  @input="errors.middleName.hasError = false"
                >
                  <span
                    v-if="middleName && errors.middleName.hasError"
                    slot="helper"
                  >
                    {{ errors.middleName.message }}
                  </span>
                </InputText>
                <InputText
                  v-model.trim="lastName"
                  label="Last Name"
                  placeholder="Last Name"
                  name="last-name"
                  class="mt-2"
                  :has-error="errors.lastName.hasError"
                  :disabled="ivsProofingCompleted"
                  @blur="analytics.input.lastName();"
                  @input="errors.lastName.hasError = false"
                >
                  <span
                    v-if="errors.lastName.hasError"
                    slot="helper"
                  >
                    {{ errors.lastName.message }}
                  </span>
                </InputText>
                <InputSuffix
                  v-model="suffix"
                  class="mt-2"
                  label="Suffix"
                  name="suffix"
                  placeholder="Select"
                  :disabled="ivsProofingCompleted"
                />
              </template>
              <InputText
                v-model.trim="email"
                label="Email Address"
                placeholder="Email Address"
                name="email"
                class="mt-2"
                :has-error="errors.email.hasError"
                :disabled="ivsProofingCompleted"
                @blur="analytics.input.email();"
                @input="errors.email.hasError = false"
              >
                <span
                  v-if="errors.email.hasError"
                  slot="helper"
                >
                  {{ errors.email.message }}
                </span>
              </InputText>
              <div class="mt-5">
                <label class="reminder_email--label">
                  Check this box to opt-in to receive a one-time reminder email.
                  <input
                    id="optin-mail-reminder"
                    :checked="optInMailReminder"
                    class="mt-3"
                    type="checkbox"
                    @click="optInMailReminder = !optInMailReminder; analytics.click.optInReminder(optInMailReminder)"
                  >
                  <span class="checkmark" />
                </label>
              </div>
              <div class="mt-5">
                <p>
                  By opting in, if you choose not to complete your Change-of-Address
                  (COA), USPS will send you a one-time email reminder to come back
                  and finalize your request. Your COA will not be activated until
                  it is complete.
                </p>
              </div>
              <div class="flex flex-row mt-4">
                <InputPhone
                  v-model="phoneNumber"
                  :label="phoneNumLabel"
                  placeholder="(000) 000-0000"
                  name="phone"
                  class="flex-1"
                  :has-error="errors.phoneNumber.hasError"
                  :disabled="ivsProofingCompleted"
                  @blur="analytics.input.phone();"
                  @input="errors.phoneNumber.hasError = false"
                >
                  <span
                    v-if="errors.phoneNumber.hasError"
                    slot="helper"
                  >
                    {{ errors.phoneNumber.message }}
                  </span>
                </InputPhone>
                <div class="block pl-4" />
                <InputDropdown
                  v-if="!twoFactorAuthEnabled"
                  v-model="phoneType"
                  label="Phone Type"
                  name="phone-type"
                  class="flex-1"
                  :has-error="errors.phoneType.hasError"
                  @blur="analytics.input.phoneType();"
                >
                  <option value="HOME">
                    Home
                  </option>
                  <option value="MOBILE">
                    Mobile
                  </option>
                  <span
                    v-if="errors.phoneType.hasError"
                    slot="helper"
                  >
                    {{ errors.phoneType.message }}
                  </span>
                </InputDropdown>
              </div>
              <div
                v-if="twoFactorAuthEnabled"
                class="mt-5"
              >
                <p>
                  A valid mobile phone number is now required to process your online Change-of-Address (COA)
                  request. Your mobile phone number will be used to complete online Identity Proofing and
                  Verification, in an effort to improve the security of the online COA process, and to identify
                  and mitigate potential fraud. Online Identity Proofing may include a Phone Verification
                  and One-Time Passcode solution and/or a Device Reputation Remote Identity Verification
                  solution, as applicable.
                </p>
                <div class="block pl-4 mt-4" />
              </div>
              <InputDropdown
                v-model="forwardType"
                label="Type of Move"
                class="mt-2"
                name="forward-type"
                :has-error="errors.forwardType.hasError"
                @input="$event && analytics.click.forwardType($event);"
              >
                <option
                  value=""
                  aria-describedby="mover-type-field"
                >
                  Select
                </option>
                <option value="PERMANENT">
                  Permanent
                </option>
                <option value="TEMPORARY">
                  Temporary
                </option>
                <span
                  v-if="errors.forwardType.hasError"
                  slot="helper"
                >
                  {{ errors.forwardType.message }}
                </span>
              </InputDropdown>
              <InputDatePicker
                v-model="startDate"
                :label="forwardType !== 'TEMPORARY' ? 'Mail Forwarding Date' : 'Start Forwarding'"
                :min="minStartDate"
                :max="maxStartDate"
                desktop
                name="forward-start-date"
                class="mt-2"
                :has-error="errors.startDate.hasError"
                @input="analytics.input.startDate();"
              >
                <span slot="helper">
                  {{ errors.startDate.message }}
                </span>
              </InputDatePicker>
              <InputDatePicker
                v-if="forwardType === 'TEMPORARY'"
                v-model="stopDate"
                :min="minStopDate"
                :max="maxStopDate"
                :open-date="minStopDate"
                desktop
                label="End Forwarding"
                name="forward-stop-date"
                class="mt-3"
                :has-error="errors.stopDate.hasError"
                @input="analytics.input.stopDate();"
              >
                <span slot="helper">
                  {{ errors.stopDate.message }}
                </span>
              </InputDatePicker>
            </div>
          </div>
          <div class="flex-1 pl-xl">
            <h3>More Information</h3>
            <template
              v-if="moverType === 'INDIVIDUAL'"
            >
              <p class="mt-4">
                <span class="font-bold">Individual</span><br>
                Choose this option if one person is moving. If you receive mail by more than one name (i.e. maiden name or nickname), complete a separate form for each name.
              </p>
            </template>
            <template
              v-if="moverType === 'FAMILY'"
            >
              <p class="mt-4">
                <span class="font-bold">Family</span><br>
                Choose this option and complete one form if everyone in your family has the same last name. If not all family members are moving, OR if some family members have different last names, choose the Individual option and submit a separate order for each mover.
              </p>
            </template>
            <!-- Individual and Family messaging -->
            <template v-if="moverType === 'INDIVIDUAL' || moverType === 'FAMILY'">
              <p class="mt-4">
                <span class="font-bold">First Name (or Initial)</span><br>
                Enter your first name or initial.
              </p>
              <p class="mt-4">
                <span class="font-bold">Middle Name (or Initial)</span><br>
                Enter your middle name or initial.
              </p>
              <p class="mt-4">
                <span class="font-bold">Last Name</span><br>
                Fill in only one last name. If anyone with the same last name is moving to a different address, use a separate Change-of-Address for each person.
              </p>
            </template>
            <!-- Business messaging -->
            <template v-if="moverType === 'BUSINESS'">
              <p class="mt-4">
                <span class="font-bold">Business</span><br>
                Choose this option if your business is moving. Individual or Family orders that included a business address as the old address will not be processed. Existing or former employees cannot have their "personal" mail forwarded from a business.
              </p>
              <p class="mt-4">
                <span class="font-bold">Business Name</span><br>
                Enter the primary business name in this field.
                <template v-if="flagBusinessAliasEnabled">
                  If you receive mail by more than one business name, please include each business alias name in the expandable fields.
                </template>
              </p>
              <p
                v-if="flagBusinessAliasEnabled"
                class="mt-4"
              >
                Each alias name added will be treated as its own Change-of-Address order. Only one email address will be collected and applied to all primary and alias names.
              </p>
            </template>
            <!-- Permanent forwardType messaging -->
            <template v-if="forwardType === 'PERMANENT'">
              <p class="mt-4">
                <span class="font-bold">Permanent</span><br>
                Choose this option if you don't plan to move back to your old address (most moves are permanent).
              </p>
            </template>
            <!-- Temporary and default forwardType messaging-->
            <template v-if="!forwardType || forwardType === '' || forwardType === 'TEMPORARY'">
              <p class="mt-4">
                <span class="font-bold">Temporary</span><br>
                Choose this option if you plan to move back to your old address in less than six months.
              </p>
              <p class="mt-4">
                If you plan on moving to a new address for less than 6 months, then plan on moving to a different residence that's not the same as your old address, choose "Permanent," and fill out another Change-of-Address request at that time.
              </p>
            </template>
            <p class="mt-4">
              <span class="font-bold">Mail Forwarding Date</span><br>
              Enter the date you'd like us to begin forwarding your mail to your new address. The date you enter must not be more than 30 days prior to today, or more than 3 months from today. Mail should arrive at your new address 7-10 postal business days after your move effective date OR your file date, whichever date is later.
            </p>
            <template v-if="forwardType !== 'PERMANENT'">
              <p class="mt-4">
                The minimum length for filing a temporary Change-of-Address is 15 days.
              </p>
              <p class="mt-4">
                The initial forwarding period is restricted to 6 months, but can be extended up to 1 year.
              </p>
            </template>
          </div>
        </div>
        <div class="c-hairline" />
        <div class="py-8 flex flex-row flex-no-wrap">
          <div class="flex-1">
            <div class="max:pr-xl">
              <h3>What's your old address?</h3>
              <InputZipCode
                v-model="oldZipCode"
                label="ZIP Code™"
                placeholder="00000"
                name="old-zip-code"
                class="mt-4"
                :has-error="errors.oldZipCode.hasError"
                :async-fn="zipSearch({
                  cityName: oldCity,
                  state: oldState,
                })"
                :transform-compare="zipCompare"
                :transform-label="zipLabel"
                :transform-value="zipValue"
                :disabled="ivsProofingCompleted"
                @blur="analytics.input.oldZipCode();"
                @select="oldCity = $event.cityName;
                         oldState = $event.state"
              >
                <template slot="helper">
                  {{ errors.oldZipCode.message | exceptionNextToSave }}
                </template>
              </InputZipCode>
              <InputListBox
                v-model="oldCity"
                class="mt-2"
                label="City"
                name="old-city"
                placeholder="City"
                :has-error="errors.oldCity.hasError"
                :async-fn="citySearch({
                  zip: oldZipCode,
                  state: oldState,
                })"
                :transform-compare="zipCompare"
                :transform-label="cityLabel"
                :transform-value="cityValue"
                :disabled="ivsProofingCompleted"
                @blur="analytics.input.oldCity();"
                @select="oldZipCode = $event.zip; oldState = $event.state"
              >
                {{ errors.oldCity.message }}
              </InputListBox>
              <InputState
                v-model="oldState"
                label="State"
                placeholer="State"
                name="old-state"
                class="mt-2"
                :has-error="errors.oldState.hasError"
                :disabled="ivsProofingCompleted"
                @input="analytics.input.oldState();"
              >
                <span
                  v-if="errors.oldState.hasError"
                  slot="helper"
                >
                  {{ errors.oldState.message }}
                </span>
              </InputState>
              <InputListBox
                v-model="oldStreetAddress"
                class="mt-2"
                label="Street Address"
                name="old-street-address"
                placeholder="Street Address"
                :has-error="errors.oldStreetAddress.hasError"
                :async-fn="streetSearch({
                  cityName: oldCity,
                  state: oldState,
                  zipcode: oldZipCode,
                })"
                :transform-compare="streetCompare"
                :transform-label="streetLabel"
                :transform-value="streetValue"
                :disabled="ivsProofingCompleted"
                @input="oldPoBoxNumber = ''"
                @blur="analytics.input.oldStreet();"
                @select="oldZipCode=$event.zip;
                         oldCity=$event.cityName;
                         oldState=$event.state;"
              >
                {{ errors.oldStreetAddress.message | exceptionNextToSave }}
              </InputListBox>
              <InputText
                v-if="oldState === 'PR'"
                v-model="oldUrbanizationName"
                label="Urbanization Name (Puerto Rico only)"
                name="old-urbanization-name"
                placeholder="URB HIGHLAND GDNS"
                class="mt-2"
                :has-error="errors.oldUrbanizationName.hasError"
                :disabled="ivsProofingCompleted"
                @blur="analytics.input.oldUrbanizationName();"
              >
                <span slot="helper">
                  {{ errors.oldUrbanizationName.message }}
                </span>
              </InputText>
              <InputText
                v-if="showOldPoBox"
                v-model="oldPoBoxNumber"
                label="Po Box"
                name="old-po-box"
                placeholder="PO Box 123"
                class="mt-2"
                :has-error="errors.oldPoBoxNumber.hasError"
                :disabled="ivsProofingCompleted"
                @blur="analytics.input.oldPoBox();"
                @input="oldAddressError = ''"
              >
                <template slot="helper">
                  {{ errors.oldPoBoxNumber.message | exceptionNextToSave }}
                </template>
              </InputText>
            </div>
          </div>
          <div class="flex-1 pl-xl">
            <p>
              This is the address you are moving from. Enter a valid address, and remember to include apartment/suite number if applicable.
            </p>
            <p class="mt-2">
              If your address is a PO Box, enter it in the Street Address field for example, PO Box 1001. Please note that Change-of-Address request from non-USPS Private Mail Boxes (PMB) cannot be processed.
            </p>
            <p
              v-if="oldPoBoxException"
              class="errors text-red-200 mt-2"
            >
              <span class="font-bold">Note:</span> The address you provided is not considered a proper mailing address within this ZIP Code™. A proper mailing address within this ZIP Code™ requires a PO Box address. Please input your PO Box number in the “PO Box” field and click "Next" to continue.<br><span class="font-bold">A PO Box is required to proceed.</span>
            </p>
          </div>
        </div>
        <div class="c-hairline" />
        <div class="py-8 flex flex-row flex-no-wrap">
          <div class="flex-1">
            <div class="max:pr-xl">
              <h3>What's your new address?</h3>
              <InputZipCode
                v-model="newZipCode"
                label="ZIP Code™"
                name="new-zip-code"
                placeholder="00000"
                class="mt-4"
                :has-error="errors.newZipCode.hasError"
                :async-fn="zipSearch({
                  cityName: newCity,
                  state: newState,
                })"
                :transform-compare="zipCompare"
                :transform-label="zipLabel"
                :transform-value="zipValue"
                @blur="analytics.input.newZipCode();"
                @select="newCity = $event.cityName;
                         newState = $event.state"
              >
                <template slot="helper">
                  {{ errors.newZipCode.message | exceptionNextToSave }}
                </template>
              </InputZipCode>
              <InputListBox
                v-model="newCity"
                label="City"
                placeholder="City"
                name="new-city"
                class="mt-2"
                :has-error="errors.newCity.hasError"
                :async-fn="citySearch({
                  zip: newZipCode,
                  state: newState,
                })"
                :transform-compare="zipCompare"
                :transform-label="cityLabel"
                :transform-value="cityValue"
                @blur="analytics.input.newCity();"
                @select="newZipCode = $event.zip; newState = $event.state"
              >
                {{ errors.newCity.message }}
              </InputListBox>
              <InputState
                v-model="newState"
                label="State"
                name="new-state"
                placeholer="State"
                class="mt-2"
                :has-error="errors.newState.hasError"
                @input="analytics.input.newState();"
              >
                <span
                  v-if="errors.newState.hasError"
                  slot="helper"
                >
                  {{ errors.newState.message }}
                </span>
              </InputState>
              <InputListBox
                v-model="newStreetAddress"
                label="Street Address"
                placeholder="Street Address"
                name="new-street-address"
                class="mt-2"
                :has-error="errors.newStreetAddress.hasError"
                :async-fn="streetSearch({
                  cityName: newCity,
                  state: newState,
                  zipcode: newZipCode,
                })"
                :transform-compare="streetCompare"
                :transform-label="streetLabel"
                :transform-value="streetValue"
                @input="newPoBoxNumber = ''; newPrivateMailbox = '';"
                @blur="analytics.input.newStreet();"
                @select="newZipCode=$event.zip;
                         newCity=$event.cityName;
                         newState=$event.state;"
              >
                {{ errors.newStreetAddress.message | exceptionNextToSave }}
              </InputListBox>
              <InputText
                v-if="newState === 'PR'"
                v-model="newUrbanizationName"
                label="Urbanization Name (Puerto Rico only)"
                name="new-urbanization-name"
                placeholder="URB HIGHLAND GDNS"
                class="mt-5"
                :has-error="errors.newUrbanizationName.hasError"
                @blur="analytics.input.newUrbanizationName();"
              >
                <span slot="helper">
                  {{ errors.newUrbanizationName.message }}
                </span>
              </InputText>
              <InputText
                v-if="showNewPoBox"
                v-model="newPoBoxNumber"
                label="Po Box"
                name="new-po-box"
                placeholder="PO Box 123"
                class="mt-2"
                :has-error="errors.newPoBoxNumber.hasError"
                @blur="analytics.input.newPoBox();"
              >
                <template slot="helper">
                  {{ errors.newPoBoxNumber.message | exceptionNextToSave }}
                </template>
              </InputText>
              <InputText
                v-if="showNewPrivateMailbox"
                v-model="newPrivateMailbox"
                label="Private Mailbox"
                name="new-private-mailbox"
                placeholder="PMB 123"
                class="mt-2"
                :has-error="errors.newPrivateMailbox.hasError || !newPrivateMailbox"
                @blur="analytics.input.newPrivateMailbox();"
              >
                <template slot="helper">
                  {{ errors.newPrivateMailbox.message | exceptionNextToSave }}
                </template>
              </InputText>
            </div>
          </div>
          <div class="flex-1 pl-xl">
            <p>
              This is the address you are moving to. Enter a valid address, and remember to include apartment/suite number if applicable.
            </p>
            <p class="mt-2">
              If your address is a PO Box, enter it in the Street Address field for example, PO Box 1001. Please note that Change-of-Address request from non-USPS Private Mail Boxes (PMB) cannot be processed.
            </p>
            <p
              v-if="newPoBoxException"
              class="errors text-red-200 mt-2"
            >
              <span class="font-bold">Note:</span> The address you provided is not considered a proper mailing address within this ZIP Code™. A proper mailing address within this ZIP Code™ requires a PO Box address. Please input your PO Box number in the “PO Box” field and click "Next" to continue.<br><span class="font-bold">A PO Box is required to proceed.</span>
            </p>
          </div>
        </div>
        <div class="c-hairline" />
        <transition
          name="fade"
          appear
        >
          <section
            v-if="renderV3Challenge && !showOutageBanner"
            class="mt-8"
          >
            <p class="mb-4">
              For your security, clicking the box below confirms that this Change-of-Address form is being completed by a person, and not an automated query.
            </p>
            <vue-recaptcha
              id="recaptcha-challenge"
              ref="recaptchaChallenge"
              :sitekey="getRecaptchaV2Key()"
              @click.native="analytics.click.recaptchaVerified"
              @verify="recaptchaChallengeToken=$event;"
              @expired="recaptchaChallengeToken='';"
            />
            <transition
              appear
              name="fade"
            >
              <p
                v-if="errors.recaptchaChallengeToken && errors.recaptchaChallengeToken.hasError"
                class="c-input-text__helper c-input-helper errors"
              >
                {{ errors.recaptchaChallengeToken.message }}
              </p>
            </transition>
          </section>
        </transition>
        <div class="mt-8">
          <button
            id="verify-info-save"
            class="c-btn"
            @click="analytics.click.save();"
          >
            Save
          </button>
        </div>
      </form>
      <Modal
        id="new-constr-close-x"
        v-model="newConstructionModal"
      >
        <ModalNewConstruction
          :street-address="newStreetAddress"
          :city="newCity"
          :state="newState"
          :zip-code="newZipCode"
          :urbanization-name="newUrbanizationName"
          @reset="newConstructionModal = false"
          @override="newConstructionOverride"
        />
      </Modal>
      <Modal v-model="moverTypeFamilyModal">
        <h4>Based on your responses...</h4>
        <p
          class="mt-3"
        >
          {{
            familySameLastName === 'YES' || familyRemaining === 'YES'
              ? 'A Family move will forward all mail associated with this last name. Based on your response, an Individual Change-of-Address must be done for each person moving to ensure the mail of the person remaining is unaffected by this Change-of-Address.'
              : ''
          }}
          {{
            familySameLastName === 'NO' && familyRemaining !== 'YES'
              ? 'A Family move is only required when more than one person, with the same last name, is moving from the address. Anyone with a different last name must file an Individual Change-of-Address.'
              : ''
          }}
        </p>
        <button
          v-if="familySameLastName === 'YES' || familyRemaining === 'YES'"
          class="c-btn mt-3"
          @click="moverType='INDIVIDUAL'; moverTypeFamilyModal=false;"
        >
          Continue
        </button>
        <button
          v-if="familySameLastName === 'NO' && familyRemaining !== 'YES'"
          class="c-btn mt-3"
          @click="moverTypeFamilyModal=false;"
        >
          Continue
        </button>
      </Modal>
      <Modal v-model="moverTypeBusinessModal">
        <h4>Based on your responses...</h4>
        <p
          class="mt-3"
        >
          A Business move should only be submitted when the entire business is moving. If the entire business is moving to a new address, please select Continue, and change your response to Yes.
        </p>
        <button
          class="c-btn mt-3"
          @click="moverType='INDIVIDUAL'; moverTypeBusinessModal=false;"
        >
          Continue
        </button>
      </Modal>
      <Modal
        ref="emailReminderOptOutModal"
        v-model="emailReminderOptOutModal"
      >
        <h4>One-Time Email Opt-Out</h4>
        <p class="mt-3">
          You've opted-out of the one-time reminder email if you don't complete this Change-of-Address transaction.
        </p>
        <p class="mt-3">
          You will still receive confirmation emails if you complete your transaction.
        </p>
        <button
          class="c-btn mt-3"
          @click="emailReminderOptOutModal=false"
        >
          Close
        </button>
      </Modal>
      <Modal
        ref="coaCheckModal"
        v-model="coaCheckModal"
      >
        <h4 class="font-bold">
          Based on your response...
        </h4>
        <p class="mt-3">
          A Change-of-Address already exists matching the name and address you've entered. To make changes to your current Change-of-Address, visit
          <a
            class="c-link c-link--inline"
            :href="this.$sanitize($options.coacUrl)"
            rel="noopener noreferrer"
          >
            {{ $options.coacUrl }}
          </a>, enter your new ZIP Code™ and 16-digit confirmation code, which can be located within your confirmation email and the Confirmation Notification Letter you received at your new address.
        </p>
      </Modal>
    </div>
  </section>
</template>

<script>
/* eslint-disable default-case */
import VueRecaptcha from 'vue-recaptcha';
import {
  InputDatePicker,
  InputDropdown,
  InputListBox,
  InputPhone,
  InputRadio,
  InputState,
  InputSuffix,
  InputText,
  InputZipCode,
  Modal,
} from '@mgo/ui/lib/components';
import { desktopAnalytics } from './analytics';
import { track } from '../../analytics/events';
import ModalNewConstruction from '../../components/ModalNewConstruction.vue';

// the mixins and shared logic
import addressViolations from '../../mixins/addressViolations';
import datePicker from '../../mixins/datePicker';
import emailReminder from '../../mixins/emailReminder';
import moverType from '../../mixins/moverType';
import newConstruction from '../../mixins/newConstruction';
import rapidKey from '../../mixins/rapidKey';
import destinationManager from '../../mixins/destinationManager';
import verifyInfoEditMixin from './mixin';
import grecaptcha from '../../mixins/grecaptcha';
import SvgPlusIcon from '../../components/SvgPlusIcon.vue';
import BusinessAlias from '../../components/BusinessAlias.vue';

export default {
  name: 'DesktopVerifyInfoEdit',
  components: {
    InputDatePicker,
    InputDropdown,
    InputListBox,
    InputPhone,
    InputRadio,
    InputState,
    InputSuffix,
    InputText,
    InputZipCode,
    Modal,
    ModalNewConstruction,
    VueRecaptcha,
    SvgPlusIcon,
    BusinessAlias,
  },
  mixins: [
    addressViolations,
    datePicker,
    emailReminder,
    moverType,
    newConstruction,
    rapidKey,
    verifyInfoEditMixin,
    destinationManager,
    grecaptcha,
  ],
  computed: {
    analytics() {
      return desktopAnalytics();
    },
  },
  async mounted() {
    // enable loader for redirect
    this.$store.commit('ENABLE_LOADER');
    this.optInMailReminder = JSON.parse(sessionStorage.getItem('optInMailReminder') ?? false);

    await this.hydrateCOA();

    // redirect to disclaimer if insufficient coa data or coa already completed
    if (!this.startDate || this.cfpsid) {
      this.$router.push({ name: 'disclaimer' });
      this.$store.commit('DISABLE_LOADER');
      return;
    }

    this.hydrateMover();
    this.hydrateAddresses();

    this.$store.commit('DISABLE_LOADER');
  },
  methods: {
    async submit() {
      this.$store.commit('ENABLE_LOADER');

      if (!this.informedDeliveryEligible) {
        this.informedDelivery = false;
      }

      if (this.twoFactorAuthEnabled) {
        this.phoneType = 'MOBILE';
      }

      const { medUpdated, sdUpdated, xhr } = await this.$coa.coaUpdate({
        moverType: this.moverType,
        forwardType: this.forwardType,
        startDate: this.startDate,
        informedDelivery: this.informedDelivery,
        stopDate: this.forwardType === 'TEMPORARY' ? this.stopDate : null,
        businessName: this.moverType === 'BUSINESS' ? this.businessName : null,
        email: this.email || null,
        famPrompt1ShareLastName: this.familySameLastName
          ? this.familySameLastName === 'YES'
          : null,
        famPrompt2SameLastNameRemaining: this.familyRemaining
          ? this.familyRemaining === 'YES'
          : null,
        bizPromptEntireMoving: this.businessWholeCompany
          ? this.businessWholeCompany === 'YES'
          : null,
        multipleAddresses: this.multipleAddresses === 'YES' ?? false,
      });

      this.attachFieldErrors({ xhr });

      // start/stop date updated due to email domain check
      this.medUpdated = medUpdated;
      this.sdUpdated = sdUpdated;

      if (xhr?.exception || this.fieldErrors?.length) {
        this.$store.commit('DISABLE_LOADER');
        await this.$nextTick();
        this.scrollToFirstError();
        return;
      }

      track('CoaUpdated',
        {
          dcoa: this.dcoa,
          forwardType: this.forwardType,
          informedDelivery: this.informedDelivery,
          moverType: this.moverType,
          startDate: this.startDate,
          stopDate: this.stopDate,
        });

      let businessAliasPayload = [];
      if (this.showBusinessAlias) {
        businessAliasPayload = this.enhancedBusinessAliasesEnabled
          ? this.businessAliases.map((d) => d.value).filter((v) => v)
          : [
            this.businessAlias1,
            this.businessAlias2,
            this.businessAlias3,
            this.businessAlias4,
          ].filter((v) => v);
      }

      // Business Aliases are not eligible for EMF
      if (businessAliasPayload.length > 0) {
        await this.$coa.extensionSave(0, this.$coa.visitId);
      }

      const businessAliasHandle = this.flagBusinessAliasEnabled
        && this.moverType === 'BUSINESS' && this.$coa.businessAliasSave(businessAliasPayload);

      const personHandle = this.$coa.personSave({
        personType: 'MOVER',
        firstName: this.moverType !== 'BUSINESS' ? this.firstName : null,
        middleName: this.moverType !== 'BUSINESS' ? this.middleName : null,
        lastName: this.moverType !== 'BUSINESS' ? this.lastName : null,
        suffix: this.moverType !== 'BUSINESS' ? this.suffix : null,
        email: this.email,
        [`${this.phoneType.toLowerCase()}Phone`]: this.phoneNumber,
      });

      const [
        personResponse,
        businessAliasResponse,
      ] = await Promise.all([
        personHandle,
        businessAliasHandle,
      ]);
      sessionStorage.setItem('optInMailReminder', this.optInMailReminder);
      if (this.optInMailReminder) {
        this.$coa.emailReminderOptIn();
      } else {
        this.$coa.emailReminderOptOut();
      }

      this.attachFieldErrors(personResponse);
      this.attachFieldErrors(businessAliasResponse);
      this.prepareFieldErrors(personResponse);


      const addressResponses = await this.$coa.addressSaveV2({
        addressType: 'OLD',
        streetAddress: this.oldStreetAddress,
        city: this.oldCity,
        state: this.oldState,
        zipCode: this.oldZipCode,
        urbanizationName: this.oldState === 'PR'
          ? this.oldUrbanizationName
          : null,
        privateMailbox: null,
        poBoxNumber: this.oldPoBoxNumber || null,
        recentConstruction: false,
        overrideAms: false,
        moverType: this.moverType,
        forwardType: this.forwardType,
      }, {
        addressType: 'NEW',
        streetAddress: this.newStreetAddress,
        city: this.newCity,
        state: this.newState,
        zipCode: this.newZipCode,
        urbanizationName: this.newState === 'PR'
          ? this.newUrbanizationName
          : null,
        privateMailbox: this.newPrivateMailbox || null,
        poBoxNumber: this.newPoBoxNumber || null,
        recentConstruction: this.recentConstruction,
        overrideAms: this.overrideAms,
        moverType: this.moverType,
        forwardType: this.forwardType,
      });

      track('PERSON_UPDATED',
        {
          personType: 'MOVER',
          phoneType: this.phoneType,
        });

      if (addressResponses?.xhr?.exception) {
        if (addressResponses?.xhr?.exception === 'MilitaryZip5ForwardTypeException') {
          this.fieldErrors = [
            ...this.fieldErrors,
            { field: 'forwardType', message: addressResponses?.xhr?.message || '' },
          ];
          this.errors.forwardType.message = addressResponses?.xhr?.message || '';
          this.errors.forwardType.hasError = true;
        } else if (addressResponses?.xhr?.exception === 'MilitaryZip5MoverTypeException') {
          this.fieldErrors = [
            ...this.fieldErrors,
            { field: 'moverType', message: addressResponses?.xhr?.message || '' },
          ];
          this.errors.moverType.message = addressResponses?.xhr?.message || '';
          this.errors.moverType.hasError = true;
        } else if (addressResponses?.xhr?.addressType === 'NEW') {
          this.newAddressError = addressResponses?.xhr?.exception || '';
          this.attachFieldErrors(this.newAddressError, 'new');
        } else {
          this.oldAddressError = addressResponses?.xhr?.exception || '';
          this.attachFieldErrors(this.oldAddressError, 'old');
        }
      }
      if (addressResponses?.xhr?.exception === 'InvalidPrimaryPoBoxException' && this.errors?.newPoBoxNumber?.hasError) {
        this.errors.newStreetAddress.hasError = false;
        this.errors.newStreetAddress.message = '';
      }
      if (addressResponses?.xhr?.fieldErrors) {
        if (addressResponses?.xhr?.fieldErrors[0].field.indexOf('newAddress') === 0) {
          this.newAddressError = addressResponses?.xhr?.fieldErrors[0];
          this.attachFieldErrors(this.newAddressError, 'new');
        } else {
          this.oldAddressError = addressResponses?.xhr?.fieldErrors[0];
          this.attachFieldErrors(this.oldAddressError, 'old');
        }
      }

      if (addressResponses?.xhr?.fieldErrors) {
        // eslint-disable-next-line no-unused-expressions
        addressResponses?.xhr?.fieldErrors.forEach((fieldError) => {
          const fieldNameError = fieldError.field?.split('.')[1].charAt(0).toUpperCase() + fieldError.field?.split('.')[1].slice(1);
          const oldAddressField = `old${fieldNameError}`;
          const newAddressField = `new${fieldNameError}`;
          if (fieldError.field.indexOf('newAddress') === 0) {
            this.errors[newAddressField].message = fieldError.message;
            this.errors[newAddressField].hasError = true;
          } else {
            this.errors[oldAddressField].message = fieldError.message;
            this.errors[oldAddressField].hasError = true;
          }
        });
      }

      if (this.oldAddressError === 'OldAndNewAddressMatchException'
         || this.newAddressError === 'OldAndNewAddressMatchException') {
        this.newAddressError = 'OldAndNewAddressMatchException';
        this.oldAddressError = 'OldAndNewAddressMatchException';
      }

      if (this.oldAddressError === 'MilitaryAddressException'
        || this.newAddressError === 'MilitaryAddressException') {
        this.newAddressError = 'MilitaryAddressException';
        this.oldAddressError = 'MilitaryAddressException';
      }

      // log the defaultExceptions for old/new address
      if (!addressResponses?.xhr?.fieldErrors) {
        if (this.oldAddressError) {
          this.defaultException(this.oldAddressError);
        }
        if (this.newAddressError) {
          this.defaultException(this.newAddressError);
        }
      }

      const errorPageRedirect = this.evaluateErrorRedirect(addressResponses);
      if (errorPageRedirect) {
        this.$store.commit('DISABLE_LOADER');
        this.$router.push({ name: errorPageRedirect });
        return;
      }

      if (!addressResponses?.NEW?.uuid) {
        this.newConstructionEvaluateAddress(addressResponses);
      }

      if (this.newConstructionAttempts > 0) {
        this.$store.commit('DISABLE_LOADER');
        return;
      }

      // coaCheckHandle after the old/newAddress response
      const coaCheckHandle = await this.$coa.coaCheck();

      // Throw coaCheckModal if 'CoaCheckException'
      if (coaCheckHandle?.error) {
        const { exception } = coaCheckHandle.xhr;
        if (exception === 'CoaCheckException') {
          this.coaCheckModal = true;
          this.defaultException('DUPCOAEXCEPTION');
          this.$store.commit('DISABLE_LOADER');
          return;
        }
      }

      // log the amsStatusCodes on 200 responses for old/new addresses
      if (addressResponses?.addressType
         && this.isAmsMatch(addressResponses?.amsStatusCode)) {
        this.defaultException(addressResponses.amsStatusCode);
      }

      if (this.flagAmpersendEnabled) {
        this.$amp.identify({
          mgoOldZip5: this.oldZipCode,
          mgoNewZip5: this.newZipCode,
          mgoMoverType: this.moverType,
          mgoForwardType: this.forwardType,
        });
      }

      // Add Email domain start/stop banner flags to query string if API updated start/stop dates
      // Pass exception from response in case errors are cleared from a double submit
      this.next({
        ...(this.medUpdated && { medUpdated: this.medUpdated }),
        ...(this.sdUpdated && { sdUpdated: this.sdUpdated }),
      }, addressResponses?.xhr?.exception);
    },
  },
};
</script>
